import React, { useCallback, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import PropTypes from 'prop-types';

import { NotificationManager } from '@atlas/core';
import { BasePopup, Button, HeaderTitle, Icon, Label } from '@atlas/ui';

import useTranslate from 'common/I18n/Interpreter';

const OneClickSignUp = ({ data, id }) => {
  const { onClose = () => {} } = data;
  const componentRef = useRef();
  const handlePrint = useReactToPrint({ content: () => componentRef.current });
  const t = useTranslate();

  const handleContinueButtonClick = useCallback(() => {
    NotificationManager.removeNotification(id);
    onClose();
  }, [id, onClose]);

  const downloadTxt = useCallback(
    e => {
      e.preventDefault();
      const text = `${t('Id')}: ${data.clientId}\n${t('Login')}: ${data.login}\n${t('Password')}: ${
        data.password
      }`;
      const element = document.createElement('a');
      element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`);
      element.setAttribute('download', 'Credentials.txt');

      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();

      document.body.removeChild(element);
    },
    [data],
  );

  const copy = useCallback(
    e => {
      e.preventDefault();
      const text = `${t('Id')}: ${data.clientId}\n${t('Login')}: ${data.login}\n${t('Password')}: ${
        data.password
      }`;
      const inp = document.createElement('textarea');
      document.body.appendChild(inp);
      inp.value = text;
      inp.select();
      document.execCommand('copy', false);
      document.body.removeChild(inp);
    },
    [data],
  );

  return (
    <BasePopup className="signup-successful-modal">
      <HeaderTitle className="center border block-lg size-xl" title="Registration was successful">
        <div className="header-title__children">
          <button onClick={handleContinueButtonClick} type="button">
            <Icon name="close" />
          </button>
        </div>
      </HeaderTitle>

      <div className="popup__content">
        <Label className="signup-successful-modal__subtext" message="signup_report_important" />
        <Label className="signup-successful-modal__text" message="signup_report_text" />

        <div className="signup-successful-modal__inner">
          <div className="signup-successful-modal-info" ref={componentRef}>
            <div className="signup-successful-modal-info__item">
              <span className="signup-successful-modal-info__key">
                <Label message="id" />
              </span>
              <span className="signup-successful-modal-info__value">{data.clientId}</span>
            </div>
            <div className="signup-successful-modal-info__item">
              <span className="signup-successful-modal-info__key">
                <Label message="login" />
              </span>
              <span className="signup-successful-modal-info__value">{data.login}</span>
            </div>
            <div className="signup-successful-modal-info__item">
              <span className="signup-successful-modal-info__key">
                <Label message="password" />
              </span>
              <span className="signup-successful-modal-info__value">{data.password}</span>
            </div>
          </div>

          <div className="signup-successful-modal__buttons">
            <Button className="btn secondary big block" label="Copy" onClick={copy} />

            <Button className="btn secondary big block" label="Download" onClick={downloadTxt} />

            <Button className="btn secondary big block" label="Print" onClick={handlePrint} />
          </div>

          <Label
            className="signup-successful-modal__description"
            message="signup_report_description"
          />
        </div>

        <button className="btn primary big block" onClick={handleContinueButtonClick} type="button">
          <Label message="next" />
        </button>
      </div>
    </BasePopup>
  );
};

OneClickSignUp.propTypes = {
  data: PropTypes.shape({
    clientId: PropTypes.string,
    login: PropTypes.string,
    onClose: PropTypes.func,
    password: PropTypes.string,
  }).isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default OneClickSignUp;
